import type { LinesVerdict } from '@kronos/domain'

/**
 * The warning that has to arrive *before* the click, not after.
 *
 * A revision is never edited once anything depends on it (§4.2): a change
 * here saves the next revision and leaves the old one saying what those parts
 * were really built to. The verdict is `mayEditLines`'s, sent with the item
 * (`ComponentType.structure`), and `saveStructure` applies the same function —
 * so this sentence and what Save does cannot disagree.
 */
const REASON: Readonly<Record<Exclude<LinesVerdict, { editable: true }>['because'], (n: number) => string>> = {
  built: (n) => `${n} ${n === 1 ? 'unit has' : 'units have'} already been built to`,
  ordered: (n) => `${n} build order${n === 1 ? '' : 's'} already name`,
  scheduled: (n) => `${n} procedure${n === 1 ? ' or task' : 's or tasks'} already apply to`,
  stocked: (n) => `The stock ledger already has ${n} entr${n === 1 ? 'y' : 'ies'} against`,
  pinned: (n) => `${n} other parts list${n === 1 ? '' : 's'} pin`,
  superseded: () => 'Something has already replaced',
}

export function ForkNotice({
  lock,
  revision,
  dirty,
}: {
  lock: LinesVerdict | null
  revision: { readonly current: string | null; readonly next: string }
  dirty: boolean
}): JSX.Element | null {
  if (lock === null || revision.current === null) return null
  if (lock.editable) {
    return (
      <p className="ie-notice">
        Nothing has been built to <strong>{revision.current}</strong> yet, so changes here correct it in place.
      </p>
    )
  }
  return (
    <div className={`ie-notice ie-notice--fork${dirty ? ' ie-notice--armed' : ''}`} role="note">
      <strong>
        {REASON[lock.because](lock.count)} {revision.current}.
      </strong>{' '}
      {dirty ? 'Saving' : 'Changing this list'} will create <strong>{revision.next}</strong> and leave {revision.current} exactly as it
      was, so those parts still say what they were made of.
    </div>
  )
}
